import React from 'react'
import { makeObservable, observable } from 'mobx'
import * as R from 'ramda'
import { format, subMonths, addMonths } from 'date-fns'
import Anchor from '../../UniformComponents/Anchor'

class IntranetStore {
    constructor(store) {
        makeObservable(this)
        this.store = store
    }

    generalItems = [
        { icon: 'fal fa-chart-bar', label: 'Service Status Header Message', href: '/intranet/serviceStatus' },
        { icon: 'fal fa-envelope', label: 'Email Schedule', href: '/intranet/emailSchedule' },
        { icon: 'fal fa-cog', label: 'Survey Data Import (by CSV)', href: '/intranet/surveyDataImport' },
        { icon: 'fal fa-cog', label: 'Licence Manager', href: '/intranet/licences' },
        { icon: 'fal fa-cog', label: 'Licence Manager (new)', href: '/intranet/licencemanager' },
        { icon: 'fal fa-calculator', label: 'Licence Counter', href: '/intranet/licencecounter' },
        { icon: 'fal fa-chart-bar', label: 'System Completed Surveys', href: '/intranet/systemCompletedSurveys' },
        { icon: 'fal fa-fingerprint', label: 'System Unique Users', href: '/intranet/systemUniqueUsers' },
        { icon: 'fal fa-envelope', label: 'System Response Rate', href: '/intranet/systemResponseRates' },
        { icon: 'fal fa-stopwatch', label: 'System Uptime', href: '/intranet/uptime' },
    ]

    databaseItems = [
        { icon: 'fal fa-table', label: 'Data Viewer', href: '/intranet/dataViewer' },
        { icon: 'fal fa-table', label: 'Data Viewer (new)', href: '/intranet/dataViewer2' },
        { icon: 'fal fa-table', label: 'Custom Query Runner', href: '/intranet/queryRunner' },
        { icon: 'fal fa-cog', label: 'Move a product/Portfolio to a new Client', href: '/intranet/moveProductOrPortfolioToNewClient' },
    ]

    utilityItems = [
        { icon: 'fal fa-cog', label: 'Import/Export Questions', href: '/intranet/questionImpEx' },
        { icon: 'fal fa-cog', label: 'Evict queries from cache', href: '/intranet/queryCacheManager' },
        { icon: 'fal fa-cog', label: 'Bulk Survey Data Post to API tool', href: '/intranet/bulkSurveyDataPost' },
        { icon: 'fal fa-cog', label: 'Add New Explori Administrator', href: '/intranet/addNewExploriAdminUser' },
        { icon: 'fal fa-cog', label: 'Question Option Labels', href: '/intranet/questionoptionlangs' },
        { icon: 'fal fa-analytics', label: 'Manage Trackable Metrics', href: '/intranet/metrics' },
        { icon: 'fal fa-key', label: 'Api Keys', href: '/intranet/apiKey' },
        { icon: 'fal fa-analytics', label: 'Email Tracking', href: '/intranet/emailtracking' },
        { icon: 'fal fa-cog', label: 'Magic Quandrant Import', href: '/intranet/magicquadrant' },
        { icon: 'fal fa-cog', label: 'Custom Category', href: '/intranet/customCategory' },
        { icon: 'fal fa-cog', label: 'Mergefields', href: '/intranet/mergefields' },
        { icon: 'fal fa-cog', label: 'Benchmark Filters', href: '/intranet/benchmarkfilter' },
        { icon: 'fal fa-stopwatch', label: 'Completion Time Tracking', href: '/intranet/completionTimeTracking' },
        { icon: 'fal fa-analytics', label: 'Email Usage', href: '/intranet/emailusage' },
        { icon: 'fal fa-cog', label: 'Kpi Creator', href: '/intranet/kpiCreator' },
        { icon: 'fa-thin fa-file-certificate', label: 'Certification', href: '/intranet/certification' },
        { icon: 'fal fa-tags', label: 'Custom Tag', href: '/intranet/customTag' },
        { icon: 'fal fa-tags', label: 'Custom Benchmark', href: '/intranet/customBenchmark' },
    ]

    emailStatuses = [
        {
            name: 'scheduled',
            label: 'Scheduled',
        },
        {
            name: 'sending',
            label: 'Sending',
        },
        {
            name: 'paused',
            label: 'Paused',
        },
        {
            name: 'cancelled',
            label: 'Cancelled',
        },
    ]

    @observable emailData = []

    emailScheduleTableColumns = [
        {
            name: '',
            key: '',
            dataTransform: (survey, row) => (
                <Anchor href={`/?surveyUuid=${row.surveyId}`} isExternal target="_blank">Preview</Anchor>
            ),
        },
        {
            name: 'Survey Id',
            key: 'surveyId',
        },
        {
            name: 'Client',
            key: 'clientName',
        },
        {
            name: 'Survey name',
            key: 'surveyName',
        },
        {
            name: 'Subject',
            key: 'emailSubject',
        },
        {
            name: 'Number of emails',
            key: 'numberEmails',
            cellClass: 'center',
            dataTransform: (numberEmails) => {
                if (numberEmails > 5000) {
                    return (
                        <span className="emailsNumber red">{numberEmails}</span>
                    )
                }
                if (numberEmails > 1000) {
                    return (
                        <span className="emailsNumber orange">{numberEmails}</span>
                    )
                }

                return (
                    <span className="emailsNumber">{numberEmails}</span>
                )
            },
        },
        {
            name: 'Date',
            key: 'sendDateTime',
            dataTransform: (sendDateTime) => (
                <p>{format(new Date(sendDateTime), 'EEEE, LLLL d, yyyy')}</p>
            ),
        },
        {
            name: 'Time',
            key: 'sendDateTime',
            dataTransform: (sendDateTime) => (
                <p>{format(new Date(sendDateTime), 'h:mm:ss b')}</p>
            ),
        },
        {
            name: 'Status',
            key: 'scheduleStatus',
            dataTransform: (scheduleStatus) => (
                <p>{scheduleStatus.toUpperCase()}</p>
            ),
        },
    ]

    @observable serviceStatusMessage = {
        id: undefined,
        status: '',
        style: '', // old param
        collapsible: false,
        textColor: '',
        bgColor: '',
    }

    @observable allServiceStatusMessages = []

    emailFilterRequest = {
        dateFrom: format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
        dateTo: format(addMonths(new Date(), 1), 'yyyy-MM-dd'),
        status: ['scheduled', 'sending', 'paused', 'cancelled'],
    }

    options = [
        { value: 'none', label: 'none' },
        { value: 'red', label: 'red' },
        { value: 'orange', label: 'orange' },
        { value: 'green', label: 'green' },
        { value: 'white', label: 'white' },
    ];

    @observable isLoading = true

    isOnVPN = async () => {
        /**
         * backend check for IP
         * user with non VPN IP is redirected to home page
         */
        try {
            const response = await fetch('/api/intranet')
            const { status } = response
            if (status < 200 || status > 300) throw new Error('not allowed')
            this.isLoading = false
        } catch (e) {
            location.href = '/'
        }
    }

    @observable loggedInUsers = []

    getServiceStatusMessage = (e) => {
        this.serviceStatusMessage.status = e.target.value
    }

    getServiceStatusPreviewStyle = (item) => {
        this.serviceStatusMessage.style = item.value
    }

    handleChangeEmailRequest = ({ value }, propertyName) => {
        if (propertyName === 'startDate') {
            this.emailFilterRequest.dateFrom = value.format('yyyy-MM-DD')
        } else {
            this.emailFilterRequest.dateTo = value.format('yyyy-MM-DD')
        }
    }

    handleCheckedEmailStatus = (e) => {
        const { status } = this.emailFilterRequest
        if (!e.value) {
            this.emailFilterRequest.status = status.filter(
                (stat) => (stat !== e.name),
            )
        } else {
            this.emailFilterRequest.status.push(e.name)
        }
    }

    getServiceStatus = async () => {
        try {
            const result = await this.store.http.get('/intranet/serviceStatus')
            if (Array.isArray(result)) {
                const ssm = result[0]
                this.serviceStatusMessage = ssm // old endpoint returns array
            } else {
                this.serviceStatusMessage = result
            }
        } catch (err) {
            console.error(err.message)
        }
    }

    upsertServiceStatus = async (body) => {
        const isUpdate = Boolean(body.id)
        try {
            if (isUpdate) {
                await this.store.http.put(`/intranet/serviceStatus/${body.id}`, JSON.stringify(body))
            } else {
                await this.store.http.post('/intranet/serviceStatus', JSON.stringify(body))
            }
            this.store.addNotification(null, `Service status message ${isUpdate ? 'edited' : 'created'}`, 'success')
            this.getServiceStatus()
            this.getAllServiceStatus()
        } catch (err) {
            console.error(err.message)
            this.store.addNotification(null, `Error ${isUpdate ? 'editing' : 'creating'} status message`, 'error')
        }
    }

    getAllServiceStatus = async () => {
        try {
            const result = await this.store.http.get('/intranet/serviceStatus/search')
            this.allServiceStatusMessages = result
        } catch (err) {
            console.error(err.message)
            this.store.addNotification(null, 'Error getting status messages', 'error')
        }
    }

    deleteServiceStatus = async (id) => {
        try {
            await this.store.http.delete(`/intranet/serviceStatus/${id}`)
            this.store.addNotification(null, 'Service status message deleted', 'success')
            this.getAllServiceStatus()
        } catch (err) {
            console.error(err.message)
            this.store.addNotification(null, 'Error deleting status message', 'error')
        }
    }

    getLoggedinUsers = async () => {
        try {
            this.loggedInUsers = await this.store.http.get('/intranet/loggedinusers')
            this.store.numberOfLoggedinUsers = this.loggedInUsers.length
        } catch (e) {
            console.error(e)
            this.store.addNotification(null, 'Error loading logged in users', 'error')
        }
    }

    getEmailList = async () => {
        try {
            this.emailData = await this.store.http.post('/intranet/emailSchedule', JSON.stringify(this.emailFilterRequest))
        } catch (e) {
            this.store.addNotification(null, `something went wrong: ${e.message}`, 'error')
        }
    }
}
export default IntranetStore
